import React, { useEffect, useMemo, useState } from "react";
import {
  AndroidOutlined,
  AppleOutlined,
  PlusOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Card, Table, Tag, Typography, notification } from "antd";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import AppVersionRequest from "./AppVersionRequest";
import "./AppVersion.css";

const { Text } = Typography;

const formatDate = (date) => {
  if (!date) return "-";
  return moment(date).format("DD MMM YYYY, hh:mm A");
};

const titleCase = (value) => {
  if (!value) return "-";
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
};

const getListRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.appVersions)) return payload.data.appVersions;
  if (Array.isArray(payload?.data?.versionData)) return payload.data.versionData;
  return [];
};

const AppVersion = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [appVersions, setAppVersions] = useState([]);

  const fetchAppVersions = () => {
    setLoading(true);
    AppVersionRequest.list()
      .then((res) => {
        setAppVersions(getListRows(res?.data));
      })
      .catch((error) => {
        notification.error({
          message:
            error?.response?.data?.message ||
            "Failed to fetch app version list",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAppVersions();
  }, []);

  const columns = useMemo(
    () => [
      {
        title: "App",
        dataIndex: "app_type",
        key: "app_type",
        render: (value) => (
          <span className="app-version-cell-label">
            {value === "partner" ? <TeamOutlined /> : <UserOutlined />}
            {titleCase(value)}
          </span>
        ),
      },
      {
        title: "Platform",
        dataIndex: "platform",
        key: "platform",
        render: (value) => (
          <span className="app-version-cell-label">
            {value === "ios" ? <AppleOutlined /> : <AndroidOutlined />}
            {titleCase(value)}
          </span>
        ),
      },
      {
        title: "Version",
        dataIndex: "version",
        key: "version",
        render: (value) => <strong>{value || "-"}</strong>,
      },
      {
        title: "Force Update",
        dataIndex: "force_update",
        key: "force_update",
        render: (value) => {
          const isForce = value === true || value === 1 || value === "true";
          return (
            <Tag color={isForce ? "warning" : "default"}>
              {isForce ? "True" : "False"}
            </Tag>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (value) => (
          <Tag color={value === 1 ? "success" : "default"}>
            {value === 1 ? "Active" : "Inactive"}
          </Tag>
        ),
      },
      // {
      //   title: "Release Notes",
      //   dataIndex: "release_notes",
      //   key: "release_notes",
      //   render: (value) => value || "-",
      // },
      {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt",
        render: formatDate,
      },
      {
        title: "Updated At",
        dataIndex: "updatedAt",
        key: "updatedAt",
        render: formatDate,
      },
    ],
    []
  );

  return (
    <section className="app-version-page">
      <div className="app-version-header">
        <div>
          <h1 className="app-heading-med">App Version</h1>
          <Text type="secondary">Manage customer and partner app versions.</Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/app-setting/dashboard/app-version/create")}
        >
          Update Version
        </Button>
      </div>

      <Card className="app-version-card app-version-list-card" bordered={false}>
        <div className="app-version-list-toolbar">
          <Text strong>Version List</Text>
          <Button icon={<ReloadOutlined />} onClick={fetchAppVersions} loading={loading}>
            Refresh
          </Button>
        </div>

        <Table
          rowKey={(record) => record.id || `${record.app_type}-${record.platform}-${record.version}`}
          columns={columns}
          dataSource={appVersions}
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1100 }}
        />
      </Card>
    </section>
  );
};

export default AppVersion;
